import React, { useMemo, useState } from "react";
import FlightProgress from "../layout/FlightProgress";
import { getMockConfirmationViewModel } from "./confirmationMockData";
import "./ConfirmationScreen.css";

/**
 * Confirmation / Booking Status Design Preview.
 * Status screen only — no StickyBookingSummary, no editable fields.
 */
export default function ConfirmationScreen({
  confirmation: confirmationProp = null,
  stateId = "booked",
  onAction,
}) {
  const model = useMemo(
    () => confirmationProp || getMockConfirmationViewModel(stateId),
    [confirmationProp, stateId]
  );

  const handleAction = (actionId) => {
    if (typeof onAction === "function") onAction(actionId);
  };

  return (
    <div className="fe2-cf-page">
      <header className="fe2-cf-head">
        <h1 className="fe2-cf-head__title">{model.title}</h1>
      </header>

      <FlightProgress activeStep="confirmation" />

      <div className="fe2-cf-stack">
        <HeaderCard header={model.header} tone={model.tone} />
        <TimelineCard steps={model.timeline} />
        {model.next ? <NextCard next={model.next} /> : null}
        {model.hold ? <HoldCard hold={model.hold} /> : null}
        {model.bankPending ? <BankPendingCard bank={model.bankPending} /> : null}
        {model.failed ? <FailedCard failed={model.failed} /> : null}
        {model.tickets ? <TicketsCard tickets={model.tickets} /> : null}
        <FlightSummaryCard flight={model.flight} />
        <PassengersCard passengers={model.passengers} />
        {model.payment ? <PaymentSummaryCard payment={model.payment} /> : null}
        <ActionsBar
          primary={model.primaryAction}
          secondary={model.secondaryActions}
          onAction={handleAction}
        />
      </div>
    </div>
  );
}

function HeaderCard({ header, tone }) {
  const [copied, setCopied] = useState(false);
  if (!header) return null;

  const copyRef = async () => {
    if (!header.reference) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(header.reference);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className={`fe2-cf-header fe2-cf-header--${tone || "blue"}`}>
      {header.badgeLabel ? (
        <span
          className={`fe2-cf-badge fe2-cf-badge--${header.badgeTone || "blue"}`}
        >
          {header.badgeLabel}
        </span>
      ) : null}
      <h2 className="fe2-cf-header__title">{header.title}</h2>

      {header.reference ? (
        <div className="fe2-cf-ref">
          <span className="fe2-cf-ref__label">Booking Reference</span>
          <div className="fe2-cf-ref__row">
            <strong className="fe2-cf-ref__value fe2-ltr">{header.reference}</strong>
            {header.copyLabel ? (
              <button
                type="button"
                className="fe2-cf-ref__copy"
                onClick={copyRef}
              >
                {copied ? "הועתק" : header.copyLabel}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function TimelineCard({ steps }) {
  if (!steps?.length) return null;
  return (
    <section className="fe2-cf-card fe2-cf-card--timeline" aria-label="סטטוס הזמנה">
      <h2 className="fe2-cf-card__title fe2-cf-card__title--center">סטטוס</h2>
      <ol className="fe2-cf-timeline">
        {steps.map((s, index) => (
          <li
            key={s.label}
            className={`fe2-cf-timeline__item fe2-cf-timeline__item--${s.tone}`}
          >
            <div className="fe2-cf-timeline__text">
              <span className="fe2-cf-timeline__label">{s.label}</span>
              <strong className="fe2-cf-timeline__value">{s.value}</strong>
            </div>
            {index < steps.length - 1 ? (
              <span className="fe2-cf-timeline__arrow" aria-hidden>
                →
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function NextCard({ next }) {
  return (
    <section className="fe2-cf-next" aria-label={next.title}>
      <h2 className="fe2-cf-next__title">{next.title}</h2>
      <p className="fe2-cf-next__body">{next.body}</p>
    </section>
  );
}

function HoldCard({ hold }) {
  return (
    <section className="fe2-cf-card fe2-cf-card--amber" aria-label="Hold">
      <h2 className="fe2-cf-card__title">פרטי Hold</h2>
      <dl className="fe2-cf-facts">
        <div>
          <dt>{hold.expiresLabel}</dt>
          <dd>{hold.expiresValue}</dd>
        </div>
        <div>
          <dt>{hold.priceGuaranteeLabel}</dt>
          <dd>{hold.priceGuaranteeValue}</dd>
        </div>
        <div>
          <dt>{hold.amountDueLabel}</dt>
          <dd className="fe2-ltr fe2-cf-facts__emphasis">{hold.amountDueValue}</dd>
        </div>
      </dl>
      <p className="fe2-cf-warn">{hold.warning}</p>
    </section>
  );
}

function TicketsCard({ tickets }) {
  return (
    <section className="fe2-cf-card fe2-cf-card--green" aria-label="כרטיסים">
      <h2 className="fe2-cf-card__title">מידע כרטיסים</h2>
      <ul className="fe2-cf-tickets">
        {tickets.map((t) => (
          <li key={t.name} className="fe2-cf-tickets__item">
            <strong className="fe2-ltr">{t.name}</strong>
            <span className="fe2-cf-tickets__meta">
              {t.ticketLabel}:{" "}
              <span className="fe2-ltr">{t.ticketNumber}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BankPendingCard({ bank }) {
  return (
    <section className="fe2-cf-card fe2-cf-card--amber" aria-label="העברה">
      <h2 className="fe2-cf-card__title">העברה בנקאית</h2>
      <div className="fe2-cf-amount-row">
        <span>{bank.amountLabel}</span>
        <strong className="fe2-ltr">{bank.amountValue}</strong>
      </div>
      <p className="fe2-cf-note">{bank.note}</p>
    </section>
  );
}

function FailedCard({ failed }) {
  return (
    <section className="fe2-cf-card fe2-cf-card--red" aria-label="כשל">
      <h2 className="fe2-cf-card__title">מה ידוע</h2>
      <dl className="fe2-cf-facts">
        <div>
          <dt>{failed.paymentLabel}</dt>
          <dd className="fe2-cf-facts__fail">{failed.paymentValue}</dd>
        </div>
        <div>
          <dt>{failed.bookingLabel}</dt>
          <dd className="fe2-cf-facts__fail">{failed.bookingValue}</dd>
        </div>
      </dl>
      <p className="fe2-cf-warn fe2-cf-warn--red">{failed.note}</p>
    </section>
  );
}

function FlightSummaryCard({ flight }) {
  return (
    <section className="fe2-cf-card" aria-label="סיכום טיסה">
      <h2 className="fe2-cf-card__title">סיכום הזמנה</h2>
      <p className="fe2-cf-flight__line">
        <span className="fe2-ltr">{flight.airlineLabel}</span>
        {" · "}
        {flight.fareFamily}
      </p>
      <p className="fe2-cf-flight__route fe2-ltr">{flight.routeLabel}</p>
      <p className="fe2-cf-flight__meta">
        <span className="fe2-ltr">{flight.datesLabel}</span>
        {" · "}
        {flight.passengersLabel}
      </p>
      <div className="fe2-cf-amount-row">
        <span>Total</span>
        <strong className="fe2-ltr">{flight.totalLabel}</strong>
      </div>
    </section>
  );
}

function PassengersCard({ passengers }) {
  return (
    <section className="fe2-cf-card" aria-label="נוסעים">
      <h2 className="fe2-cf-card__title">נוסעים</h2>
      <ul className="fe2-cf-pax">
        {(passengers || []).map((p) => (
          <li key={p.id} className="fe2-ltr">
            {p.name}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PaymentSummaryCard({ payment }) {
  return (
    <section className="fe2-cf-card" aria-label="תשלום">
      <h2 className="fe2-cf-card__title">תשלום</h2>
      <dl className="fe2-cf-pay">
        <div className="fe2-cf-pay__row">
          <dt>{payment.methodLabel}</dt>
          <dd>{payment.methodValue}</dd>
        </div>
        <div className="fe2-cf-pay__row">
          <dt>{payment.amountLabel}</dt>
          <dd className="fe2-ltr">{payment.amountValue}</dd>
        </div>
        <div className="fe2-cf-pay__row">
          <dt>{payment.statusLabel}</dt>
          <dd className={`fe2-cf-tone--${payment.statusTone}`}>
            {payment.statusValue}
          </dd>
        </div>
      </dl>
      {payment.splits?.length ? (
        <ul className="fe2-cf-splits">
          {payment.splits.map((s) => (
            <li key={s.label} className="fe2-cf-splits__row">
              <span>{s.label}</span>
              <span className="fe2-ltr">{s.amount}</span>
              <span className={`fe2-cf-tone--${s.tone}`}>{s.status}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ActionsBar({ primary, secondary, onAction }) {
  return (
    <section className="fe2-cf-actions" aria-label="פעולות">
      {primary ? (
        <button
          type="button"
          className="fe2-cf-actions__primary"
          onClick={() => onAction(primary.id)}
        >
          {primary.label}
        </button>
      ) : null}
      <div className="fe2-cf-actions__secondary">
        {(secondary || []).map((a) => (
          <button
            key={a.id}
            type="button"
            className="fe2-cf-actions__btn"
            onClick={() => onAction(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>
    </section>
  );
}
